import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    MagnifyingGlass, FunnelSimple, Plus, Users, ArrowsClockwise, Star,
    UserCircle, PhoneCall, CaretRight, Storefront, Hash,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import RoleGuard from "../../components/RoleGuard";
import OwnershipToggle from "../../components/OwnershipToggle";
import OwnerCell from "../../components/OwnerCell";
import ReassignOwnerDialog from "../../components/ReassignOwnerDialog";
import CopyCodeButton from "../../components/CopyCodeButton";
import {
    ShowArchivedToggle, ArchivedBadge, ArchiveRowButton, UnarchiveRowButton, useArchiveAction,
} from "../../components/ArchiveControls";
import FormDialog, { FormDialogCancel } from "../../components/FormDialog";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

const STATUSES = [
    { key: "New", tone: "slate", hint: "Just captured" },
    { key: "Contacted", tone: "indigo", hint: "Outreach initiated" },
    { key: "Demo", tone: "violet", hint: "Demo delivered" },
    { key: "Proposal", tone: "amber", hint: "Proposal sent" },
    { key: "Won", tone: "emerald", hint: "Closed won" },
    { key: "Lost", tone: "rose", hint: "Closed lost" },
];

const STATUS_TONE = {
    New: "bg-slate-50 text-slate-700 border-slate-200",
    Contacted: "bg-indigo-50 text-indigo-700 border-indigo-200",
    Demo: "bg-violet-50 text-violet-700 border-violet-200",
    Proposal: "bg-amber-50 text-amber-700 border-amber-200",
    Won: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Lost: "bg-rose-50 text-rose-700 border-rose-200",
};

const SOURCES = ["Referral", "Website", "Cold Outreach", "Event", "Partner", "Inbound", "Other"];

export default function LeadsList() {
    const nav = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    // Iter 51.2 — Archive UI is locked to the primary super-admin only.
    const isPrimaryAdmin = !!user?.is_primary_admin;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: "", source: "", search: "" });
    const [mine, setMine] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [reassignTarget, setReassignTarget] = useState(null);
    // Iter 51.1 — Archive
    const [showArchived, setShowArchived] = useState(false);
    const archive = useArchiveAction({
        entityType: "Lead",
        archiveUrl: (row) => `/leads/${row.id}/archive`,
        unarchiveUrl: (row) => `/leads/${row.id}/unarchive`,
        onDone: () => load(),
        describe: (row) => (
            <>
                <div><span className="reedos-eyebrow">Lead</span>{" "}
                    <span className="text-slate-900 font-semibold">{row.name}</span></div>
                {row.company && (
                    <div className="mt-1"><span className="reedos-eyebrow">Company</span>{" "}
                        <span className="text-slate-800">{row.company}</span></div>
                )}
                <div className="mt-1"><span className="reedos-eyebrow">Status</span>{" "}
                    <span className="text-slate-800">{row.status}</span></div>
            </>
        ),
    });

    const load = async () => {
        setLoading(true);
        try {
            const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
            params.mine = mine;
            if (showArchived) params.include_archived = true;
            const { data: d } = await api.get("/leads/list", { params });
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load leads"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, mine, showArchived]);

    if (loading && !data) return <div data-testid="leads-page-loading"><PageHeader eyebrow="Pre-Sales" title="Loading leads…" /></div>;
    if (!data) return null;

    const counts = data.status_counts || {};

    return (
        <div data-testid="leads-list-page">
            <PageHeader
                eyebrow="Pre-Sales Pipeline"
                title={
                    <span className="flex items-center gap-3">
                        <Users size={22} weight="duotone" className="text-indigo-600" />
                        Leads
                    </span>
                }
                description="Track prospects from first contact to converted proposal. Lead and demo tracking stay fully isolated from billing."
                actions={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="leads-refresh-btn">
                            <ArrowsClockwise size={13} /> Refresh
                        </Button>
                        <RoleGuard permission="lead:write">
                            <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5" data-testid="leads-new-btn">
                                <Plus size={13} /> New lead
                            </Button>
                        </RoleGuard>
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Status summary strip */}
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-2" data-testid="leads-status-strip">
                    {STATUSES.map((s) => {
                        const active = filters.status === s.key;
                        return (
                            <button
                                key={s.key}
                                onClick={() => setFilters({ ...filters, status: active ? "" : s.key })}
                                className={classJoin(
                                    "text-left reedos-card p-3 border-2 transition-all",
                                    active ? `border-${s.tone}-500 ring-1 ring-${s.tone}-200` : "border-transparent hover:border-slate-200"
                                )}
                                data-testid={`leads-status-tile-${s.key.toLowerCase()}`}
                            >
                                <div className={classJoin("reedos-eyebrow text-[9px]", STATUS_TONE[s.key].split(" ")[1])}>
                                    {s.key}
                                </div>
                                <div className="font-display font-bold reedos-data text-2xl mt-1 leading-none">
                                    {counts[s.key] || 0}
                                </div>
                                <div className="text-[10px] text-slate-500 mt-0.5">{s.hint}</div>
                            </button>
                        );
                    })}
                </div>

                {/* Filters */}
                <div className="reedos-card p-4" data-testid="leads-filters">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="sm:col-span-2">
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Search</Label>
                            <div className="relative mt-1">
                                <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input
                                    value={filters.search}
                                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                    placeholder="Name, company, or email…"
                                    className="pl-8 h-9 text-sm"
                                    data-testid="leads-filter-search"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Source</Label>
                            <select
                                value={filters.source}
                                onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                                className="mt-1 w-full h-9 px-2 text-xs border border-slate-200 rounded bg-white"
                                data-testid="leads-filter-source"
                            >
                                <option value="">All sources</option>
                                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="flex items-end gap-2">
                            <OwnershipToggle value={mine} onChange={setMine} testId="leads-ownership-toggle" />
                            {isPrimaryAdmin && (
                                <ShowArchivedToggle
                                    value={showArchived}
                                    onChange={setShowArchived}
                                    count={(data?.items || []).filter((l) => l.deleted_at).length}
                                    testid="leads-show-archived"
                                />
                            )}
                            <Button
                                variant="ghost" size="sm"
                                onClick={() => { setFilters({ status: "", source: "", search: "" }); setMine(false); setShowArchived(false); }}
                                className="text-[11px]"
                                data-testid="leads-filter-reset"
                            >
                                Reset
                            </Button>
                        </div>
                    </div>
                </div>

                {/* List */}
                <div className="reedos-card overflow-hidden" data-testid="leads-table">
                    {data.items.length === 0 ? (
                        <EmptyState
                            icon={<Users size={36} weight="thin" />}
                            title="No leads yet"
                            subtitle="Capture your first lead to start the pre-sales flow."
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Contact / Company</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Source</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">State / GST</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Status</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Owner</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Updated</th>
                                        <th className="px-4 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.items.map((l) => (
                                        <tr
                                            key={l.id}
                                            className={`border-t border-slate-100 reedos-row-hover cursor-pointer ${l.deleted_at ? "opacity-60 bg-slate-50/60" : ""}`}
                                            onClick={() => nav(`/leads/${l.id}`)}
                                            data-testid={`leads-row-${l.id}`}
                                        >
                                            <td className="px-4 py-2.5 text-xs">
                                                <div className="font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                                                    <UserCircle size={13} weight="duotone" className="text-indigo-400" />
                                                    {l.name}
                                                    {l.deleted_at && <ArchivedBadge testid={`lead-archived-badge-${l.id}`} />}
                                                </div>
                                                {/* Iter 49.0 — lead_code surfaced for operator
                                                    socialisation with external systems. */}
                                                {l.lead_code && (
                                                    <div
                                                        className="text-[10px] font-mono text-slate-400 mt-0.5 flex items-center"
                                                        title="Lead code (natural key — used for CSV import & external system references)"
                                                    >
                                                        <span data-testid={`lead-code-${l.id}`}>{l.lead_code}</span>
                                                        <CopyCodeButton
                                                            code={l.lead_code}
                                                            label="lead code"
                                                            testid={`lead-code-copy-${l.id}`}
                                                        />
                                                    </div>
                                                )}
                                                <div className="text-slate-600 flex items-center gap-1.5 mt-0.5">
                                                    <Storefront size={11} className="text-slate-400" />
                                                    {l.company}
                                                </div>
                                                {l.contact_email && <div className="text-[10px] text-slate-500 mt-0.5 font-mono">{l.contact_email}</div>}
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-slate-600">{l.source || "—"}</td>
                                            <td className="px-4 py-2.5 text-xs">
                                                {l.state_code && <div className="font-mono font-semibold text-slate-800">{l.state_code}</div>}
                                                {l.gst_type && <div className="text-[10px] text-slate-500 font-mono">{l.gst_type}</div>}
                                                {l.gstin && <div className="text-[10px] text-slate-400 font-mono">{l.gstin}</div>}
                                            </td>
                                            <td className="px-4 py-2.5 text-xs">
                                                <span className={classJoin("inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold border", STATUS_TONE[l.status] || STATUS_TONE.New)}>
                                                    {l.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2.5">
                                                <OwnerCell
                                                    owner={l.owner}
                                                    testId={`leads-owner-${l.id}`}
                                                    onReassignClick={isSuperAdmin ? () => setReassignTarget(l) : undefined}
                                                />
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-slate-500">{formatDate(l.updated_at)}</td>
                                            <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                                                <div className="inline-flex items-center gap-1.5">
                                                    {isPrimaryAdmin && (
                                                        l.deleted_at
                                                            ? <UnarchiveRowButton onClick={() => archive.openUnarchive(l)} testid={`lead-unarchive-${l.id}`} />
                                                            : <ArchiveRowButton onClick={() => archive.openArchive(l)} testid={`lead-archive-${l.id}`} />
                                                    )}
                                                    <CaretRight size={14} className="text-slate-300" />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <NewLeadDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(lead) => {
                    setCreateOpen(false);
                    toast.success(`Lead created for ${lead.company}`);
                    nav(`/leads/${lead.id}`);
                }}
            />
            <ReassignOwnerDialog
                open={Boolean(reassignTarget)}
                onOpenChange={(o) => { if (!o) setReassignTarget(null); }}
                entityLabel="Lead"
                entityName={reassignTarget?.company}
                currentOwner={reassignTarget?.owner}
                reassignUrl={reassignTarget ? `/leads/${reassignTarget.id}/owner` : ""}
                onSuccess={load}
                testIdPrefix="lead-reassign"
            />
            {/* Iter 51.1 — Archive/Unarchive confirm dialog */}
            {archive.dialog}
        </div>
    );
}

function NewLeadDialog({ open, onClose, onCreated }) {
    const blank = {
        // Iter 49.0 — operator-supplied natural key. Auto-defaulted to system id when blank.
        lead_code: "",
        name: "", company: "", contact_email: "", contact_phone: "",
        source: "Inbound", segment: "", gstin: "", state_code: "", gst_type: "", notes: "",
        // Iter 66.6 — billing address captured upfront so client auto-create
        // on convert-to-proposal needs zero re-entry.
        address_line1: "", city: "", pin_code: "",
    };
    const [form, setForm] = useState(blank);
    const [busy, setBusy] = useState(false);
    const [enriching, setEnriching] = useState(false);
    const baselineRef = useRef(JSON.stringify(blank));

    useEffect(() => {
        if (open) {
            setForm(blank);
            baselineRef.current = JSON.stringify(blank);
        }
        // eslint-disable-next-line
    }, [open]);

    const isDirty = JSON.stringify(form) !== baselineRef.current;

    const enrich = async () => {
        if (!form.gstin || form.gstin.length !== 15) return;
        setEnriching(true);
        try {
            const { data } = await api.post("/leads/enrich-gstin", { gstin: form.gstin });
            if (!data.valid) {
                toast.warning("GSTIN format doesn't look valid — saved as-is.");
                return;
            }
            setForm((f) => ({
                ...f,
                state_code: data.state_code || f.state_code,
                gst_type: data.gst_type || f.gst_type,
            }));
            toast.success(`GSTIN valid · ${data.state_code} · ${data.gst_type}`);
        } catch (err) {
            toast.error(formatApiError(err, "Enrichment failed"));
        } finally {
            setEnriching(false);
        }
    };

    const submit = async () => {
        if (!form.name || !form.company) {
            toast.error("Name and Company are required");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/leads/create", form);
            baselineRef.current = JSON.stringify(form);  // bypass discard prompt
            onCreated(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create lead"));
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <FormDialog
            open={open}
            onOpenChange={(o) => { if (!o) onClose(); }}
            isDirty={isDirty}
            contentClassName="sm:max-w-lg max-h-[90vh] overflow-y-auto"
            testId="new-lead-dialog"
            title={<span className="flex items-center gap-2"><Plus size={18} weight="duotone" className="text-indigo-600" />New lead</span>}
            description="Minimum-friction entry — just name + company. Paste a GSTIN for auto-fill."
        >
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Contact name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="lead-field-name" placeholder="Priya Shah" />
                        <Field label="Company *" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="lead-field-company" placeholder="Velocity Logistics" />
                    </div>
                    {/* Iter 49.0 — Natural-key lead_code (auto-defaulted to system id when blank). */}
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Lead code</Label>
                        <Input
                            value={form.lead_code}
                            onChange={(e) => setForm({ ...form, lead_code: e.target.value })}
                            placeholder="optional · e.g. VELOCITY-001 · auto-assigned if blank"
                            className="mt-1 h-9 font-mono text-xs"
                            maxLength={64}
                            data-testid="lead-field-lead-code"
                        />
                        <p className="mt-1 text-[10px] text-slate-500">
                            Operator-supplied natural key. Used by CSV import &amp; external system references. Leave blank to auto-default to the system id.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Email" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="lead-field-email" />
                        <Field label="Phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="lead-field-phone" />
                    </div>
                    <div className="rounded-md border border-indigo-200 bg-indigo-50/30 p-3 space-y-2">
                        <div className="flex items-center gap-2">
                            <Hash size={13} className="text-indigo-700" />
                            <Label className="text-[10px] font-semibold text-indigo-800 uppercase">GSTIN · auto-enrich</Label>
                        </div>
                        <div className="flex gap-2">
                            <Input
                                value={form.gstin}
                                onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                                onBlur={enrich}
                                placeholder="27AAAAA0000A1Z5 · blurs to fetch state + gst type"
                                className="flex-1 h-9 font-mono text-xs"
                                maxLength={15}
                                data-testid="lead-field-gstin"
                            />
                            <Button size="sm" variant="outline" onClick={enrich} disabled={enriching || !form.gstin} data-testid="lead-enrich-btn" className="gap-1">
                                <Star size={12} weight="fill" /> {enriching ? "…" : "Enrich"}
                            </Button>
                        </div>
                        {(form.state_code || form.gst_type) && (
                            <div className="text-[11px] text-indigo-800 flex gap-4" data-testid="lead-enrich-result">
                                State: <strong className="font-mono">{form.state_code || "—"}</strong>
                                GST type: <strong className="font-mono">{form.gst_type || "—"}</strong>
                            </div>
                        )}
                    </div>
                    {/* Iter 66.6 — Billing address captured upfront. Optional
                        but if filled, flows verbatim onto the auto-created
                        client at convert-to-proposal time (no re-entry). */}
                    <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3 space-y-2">
                        <Label className="text-[10px] font-semibold text-slate-600 uppercase">Billing address (optional)</Label>
                        <Field label="Address line" value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} testid="lead-field-address1" placeholder="e.g. 401, Lotus Tower, BKC" />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="lead-field-city" placeholder="Mumbai" />
                            <Field label="PIN code" value={form.pin_code} onChange={(v) => setForm({ ...form, pin_code: v.replace(/\D/g, "").slice(0, 6) })} testid="lead-field-pin" placeholder="400051" />
                        </div>
                        <div className="text-[10px] text-slate-500">
                            Auto-flows onto the billing client when this lead converts. Skippable if not yet known.
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Source</Label>
                            <select
                                value={form.source}
                                onChange={(e) => setForm({ ...form, source: e.target.value })}
                                className="mt-1 w-full h-9 px-2 text-sm border border-slate-200 rounded bg-white"
                                data-testid="lead-field-source"
                            >
                                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <Field label="Segment" value={form.segment} onChange={(v) => setForm({ ...form, segment: v })} testid="lead-field-segment" placeholder="Enterprise / SMB / Govt…" />
                    </div>
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Notes</Label>
                        <textarea
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Intro context, pain points, referrer…"
                            rows={2}
                            className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded resize-none"
                            data-testid="lead-field-notes"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <FormDialogCancel variant="ghost" testId="lead-create-cancel">Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={busy} data-testid="lead-create-submit" className="gap-1.5">
                        <PhoneCall size={13} /> {busy ? "Saving…" : "Save lead"}
                    </Button>
                </DialogFooter>
        </FormDialog>
    );
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
    return (
        <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase">{label}</Label>
            <Input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="mt-1 h-9 text-sm"
                data-testid={testid}
            />
        </div>
    );
}
